import jwtDecode from 'jwt-decode'
import { UsuarioPerfil } from 'src/tipos'

import { normalizarPerfisJwt } from './funcoes'
import { Perfil, Usuario } from './tipos'

/**
 * Gravação da sessão do portal no localStorage.
 *
 * Este módulo existe para que o formato de `@ipgs/usuario` tenha UM dono. Antes
 * só o `AutenticacaoContainer` sabia montá-lo, e ele é um hook de contexto — não
 * dá para chamar de fora de um componente, nem de uma tela pública que não vive
 * dentro do provider. A tela de condição especial precisa gravar essa sessão no
 * momento em que manda o aluno para o portal, e sem este módulo teria de
 * duplicar o formato.
 */

export const CHAVE_USUARIO = '@ipgs/usuario'
export const CHAVE_PERFIL = '@ipgs/perfil'

/**
 * Lê as claims do JWT. Devolve `undefined` para token ausente ou malformado —
 * quem chama decide o que fazer, porque no login isso é logout e na transição
 * para o portal é apenas seguir sem sessão.
 */
export const perfilDoToken = (token: string): Perfil | undefined => {
  try {
    const claims = jwtDecode<
      {
        sub: string
        email: string
        role: keyof typeof UsuarioPerfil | Array<keyof typeof UsuarioPerfil>
        impersonated?: string
      } & Record<'impersonator_id', string | undefined>
    >(token)

    const { sub: id, email, role, impersonated } = claims

    return {
      id,
      email,
      // A claim 'role' vem como string quando o usuario tem um unico perfil
      // e como array quando tem varios; normalizamos para sempre ser array.
      perfis: normalizarPerfisJwt(role),
      impersonando: impersonated === 'true',
      impersonadorId: claims.impersonator_id
    } as Perfil
  } catch (_) {
    return undefined
  }
}

/**
 * Busca o cadastro completo do usuário, que o JWT não carrega (nome, foto,
 * endereço...). É `fetch` cru, e não `gatewayApi`, porque no login esta chamada
 * acontece antes de a instância ser recriada com o token novo.
 */
export const obterDadosDoUsuario = async (
  token: string,
  email: string
): Promise<Record<string, unknown>> => {
  const resposta = await fetch(
    `${process.env.REACT_APP_URL_GATEWAY}/api/usuario/email/${encodeURIComponent(
      email
    )}`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  if (!resposta.ok) {
    throw new Error(
      `Falha ao obter os dados do usuário (HTTP ${resposta.status}).`
    )
  }

  const corpo = await resposta.json()

  if (!corpo?.success || !corpo?.data) {
    throw new Error('Resposta inválida ao carregar o usuário.')
  }

  return corpo.data
}

/**
 * Grava a sessão do portal. Devolve o que foi gravado para quem precisa também
 * atualizar o estado em memória (o container).
 */
export const gravarSessaoDoPortal = (
  token: string,
  dadosDoUsuario: Record<string, unknown>,
  perfil: Perfil
): Usuario => {
  // foto base64 do usuario/email estoura a quota do localStorage (~5MB)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { foto: _fotoIgnorada, ...dadosSemFoto } = dadosDoUsuario
  const usuario = { token, ...dadosSemFoto } as Usuario

  localStorage.setItem(CHAVE_USUARIO, JSON.stringify(usuario))
  localStorage.setItem(CHAVE_PERFIL, JSON.stringify(perfil))

  return usuario
}

/**
 * Promove uma sessão obtida fora do portal (hoje: o link público de condição
 * especial) para a sessão do portal, de modo que o aluno não precise logar de
 * novo ao ser levado para lá.
 *
 * Funciona porque o JWT é o mesmo: tanto `POST /Auth/conta/entrar` quanto
 * `POST /propostamatricula/publica/{id}/conta` chamam o mesmo
 * `GetFullJwt(email)` no backend — mesmo emissor, audiência, chave e claims.
 *
 * **Chame apenas no instante de navegar para o portal.** Gravar `@ipgs/usuario`
 * durante o fluxo da matrícula reabriria o bug que `publica/sessao.ts` evita: o
 * interceptor do `gatewayApi` chama `sair()` em QUALQUER 401 e tira o aluno do
 * meio da matrícula.
 *
 * Nunca lança: se algo falhar, devolve `false` e quem chama segue com a
 * navegação. O pior caso é o aluno cair na tela de login — que é exatamente o
 * comportamento de hoje, não uma regressão.
 */
export const promoverSessaoParaPortal = async (
  token: string
): Promise<boolean> => {
  try {
    if (!token) return false

    const perfil = perfilDoToken(token)

    // Sem a claim de e-mail a busca do cadastro viria como
    // `/api/usuario/email/undefined`: melhor não tentar.
    if (!perfil?.email) return false

    const dadosDoUsuario = await obterDadosDoUsuario(token, perfil.email)

    gravarSessaoDoPortal(token, dadosDoUsuario, perfil)

    return true
  } catch (_) {
    return false
  }
}
